import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { automaticLoginBlocked } from './loginPreferences';
import './LoginPage.css';

function isAutofilled(input) {
  return [':autofill', ':-webkit-autofill'].some(selector => {
    try { return input.matches(selector); } catch (_) { return false; }
  });
}

function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const formRef = useRef(null);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const busy = useRef(false);
  const autoAttempted = useRef(false);
  const mounted = useRef(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const submitLogin = useCallback(async (automatic = false) => {
    if (busy.current || !mounted.current) return;
    if (automatic && (automaticLoginBlocked() || autoAttempted.current || !isAutofilled(passwordRef.current))) return;
    const email = emailRef.current.value.trim();
    const password = passwordRef.current.value;
    if (!email || !password || !emailRef.current.validity.valid) return;
    if (automatic) autoAttempted.current = true;
    busy.current = true;
    setError(''); setSaving(true);
    try {
      await login(email, password, { automatic });
      if (mounted.current) navigate('/', { replace: true });
    } catch {
      if (mounted.current) setError('Login fehlgeschlagen. Bitte prüfe die gespeicherten Zugangsdaten.');
    } finally {
      busy.current = false;
      if (mounted.current) setSaving(false);
    }
  }, [login, navigate]);

  useEffect(() => {
    mounted.current = true;
    let timer = null;
    const watchAutofill = () => {
      clearTimeout(timer);
      let checks = 0;
      const check = () => {
        if (!mounted.current) return;
        submitLogin(true);
        // Browser/password manager may fill the two fields in separate events.
        if (++checks < 20 && !busy.current && !autoAttempted.current && !automaticLoginBlocked()) timer = setTimeout(check, 120);
      };
      timer = setTimeout(check, 80);
    };
    const form = formRef.current;
    const events = ['input', 'change', 'animationstart', 'focusin'];
    events.forEach(type => form.addEventListener(type, watchAutofill));
    window.addEventListener('pageshow', watchAutofill);
    watchAutofill();
    return () => {
      mounted.current = false;
      clearTimeout(timer);
      events.forEach(type => form.removeEventListener(type, watchAutofill));
      window.removeEventListener('pageshow', watchAutofill);
    };
  }, [submitLogin]);

  return <div className="login-page">
    <form ref={formRef} className="login-card" onSubmit={event => { event.preventDefault(); submitLogin(false); }}>
      <div className="login-brand">AEONAS</div>
      <div className="login-subtitle">Tattoo Studio OS</div>
      <input ref={emailRef} name="username" type="email" placeholder="E-Mail" aria-label="E-Mail" autoComplete="username" autoCapitalize="none" spellCheck={false} required />
      <input ref={passwordRef} name="password" type="password" placeholder="Passwort" aria-label="Passwort" autoComplete="current-password" required />
      {automaticLoginBlocked() && <p className="login-hint">Du hast Dich abgemeldet. Bitte einmal auf Anmelden tippen.</p>}
      {error && <div className="login-error" role="alert">{error}</div>}
      <button type="submit" disabled={saving}>{saving ? 'Anmelden...' : 'Anmelden'}</button>
    </form>
  </div>;
}
export default LoginPage;
